import { useState } from "react";
import { auth, db } from "../utils/firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";
import LogoImage from "../assets/fav.png";
import trackEvent from "../utils/analytics";

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const Styles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,800;0,9..144,900;1,9..144,800;1,9..144,900&family=Plus+Jakarta+Sans:wght@300;400;500;600&display=swap');
    *, *::before, *::after { box-sizing: border-box; }
    .sg-root { font-family: 'Plus Jakarta Sans', sans-serif; }
    .sg-zh   { font-family: 'Fraunces', Georgia, serif; }

    @keyframes sg-slideL { from{opacity:0;transform:translateX(-18px)} to{opacity:1;transform:translateX(0)} }
    @keyframes sg-slideR { from{opacity:0;transform:translateX(18px)}  to{opacity:1;transform:translateX(0)} }
    @keyframes sg-fadeUp { from{opacity:0;transform:translateY(12px)}  to{opacity:1;transform:translateY(0)} }
    @keyframes sg-spin   { to{transform:rotate(360deg)} }
    @keyframes sg-shimmer{ 0%{background-position:-200% center} 100%{background-position:200% center} }
    @keyframes sg-pulse  { 0%,100%{opacity:1} 50%{opacity:.3} }

    .sg-panel-left  { animation: sg-slideL .55s cubic-bezier(.22,.68,0,1.2) both; }
    .sg-panel-right { animation: sg-slideR .55s .06s cubic-bezier(.22,.68,0,1.2) both; }
    .sg-f { animation: sg-fadeUp .45s both; }
    .d1{animation-delay:.08s} .d2{animation-delay:.14s} .d3{animation-delay:.20s}
    .d4{animation-delay:.26s} .d5{animation-delay:.32s} .d6{animation-delay:.38s}
    .d7{animation-delay:.44s} .d8{animation-delay:.50s}

    /* ── Shared field base ── */
    .sg-field {
      width:100%; border:1.5px solid #ebebе7;
      border-radius:9px; padding:9px 12px;
      font-size:13.5px; font-family:'Plus Jakarta Sans',sans-serif;
      color:#111; background:#fafaf9; outline:none;
      transition:border-color .15s, background .15s, box-shadow .15s;
      appearance:none; -webkit-appearance:none;
    }
    .sg-field::placeholder { color:#c8c8c4; }
    .sg-field:focus {
      border-color:#6cbb23; background:#fff;
      box-shadow:0 0 0 3px rgba(108,187,35,.09);
    }
    .sg-field:hover:not(:focus) { border-color:#d4d4d0; }

    /* select arrow */
    .sg-sel {
      background-image:url("data:image/svg+xml,%3Csvg width='11' height='7' viewBox='0 0 11 7' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4.5 4.5L10 1' stroke='%23bbb' stroke-width='1.4' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
      background-repeat:no-repeat; background-position:right 11px center;
      padding-right:30px; cursor:pointer;
    }
    .sg-sel:focus { border-color:#6cbb23; background-color:#fff; box-shadow:0 0 0 3px rgba(108,187,35,.09); }
    .sg-sel.empty { color:#c8c8c4; }

    /* textarea */
    .sg-ta { resize:none; }

    /* ── Label ── */
    .sg-lbl {
      display:block; font-size:10px; font-weight:700; color:#60605d;
      letter-spacing:.07em; text-transform:uppercase; margin-bottom:4px;
    }

    /* ── CTA button ── */
    .sg-btn {
      width:100%; border:none; cursor:pointer;
      font-family:'Plus Jakarta Sans',sans-serif; font-weight:700; font-size:14.5px;
      border-radius:11px; padding:13px;
      position:relative; overflow:hidden;
      transition:transform .18s, box-shadow .18s;
    }
    .sg-btn.on  { background:#6cbb23; color:#fff; }
    .sg-btn.on::after {
      content:''; position:absolute; inset:0;
      background:rgba(255,255,255,.12); opacity:0; transition:opacity .18s;
    }
    .sg-btn.on:hover { transform:translateY(-2px); box-shadow:0 10px 32px rgba(108,187,35,.38); }
    .sg-btn.on:hover::after { opacity:1; }
    .sg-btn.on:active { transform:scale(.97); }
    .sg-btn.off { background:#eaeae6; color:#c0c0ba; cursor:not-allowed; }
    .sg-btn.load{ background:#a8d888; color:#fff; cursor:not-allowed; }

    /* ── Custom checkbox ── */
    .sg-chk {
      width:16px; height:16px; border-radius:5px; flex-shrink:0; margin-top:1px;
      border:1.5px solid #ddd; background:#fafaf9;
      display:flex; align-items:center; justify-content:center;
      transition:background .15s, border-color .15s;
    }
    .sg-chk.on { background:#6cbb23; border-color:#6cbb23; }

    /* ── Left panel feature rows ── */
    .sg-feat { display:flex; align-items:flex-start; gap:10px; margin-bottom:12px; }
    .sg-feat-ic {
      width:28px; height:28px; border-radius:8px; flex-shrink:0;
      background:rgba(108,187,35,.14); border:1px solid rgba(108,187,35,.22);
      display:flex; align-items:center; justify-content:center; margin-top:1px;
    }

    /* ── Shimmer ── */
    .sg-shine {
      background:linear-gradient(90deg,#6cbb23 0%,#9ee050 35%,#6cbb23 55%,#4d8f10 100%);
      background-size:200% auto;
      -webkit-background-clip:text; -webkit-text-fill-color:transparent;
      background-clip:text; animation:sg-shimmer 3.5s linear infinite;
    }

    /* ── Cross deco ── */
    .sg-cx::before,.sg-cx::after { content:''; position:absolute; background:rgba(255,255,255,.1); }
    .sg-cx::before { left:50%; top:0; bottom:0; width:1.5px; transform:translateX(-50%); }
    .sg-cx::after  { top:50%; left:0; right:0; height:1.5px; transform:translateY(-50%); }

    /* ── Spinner ── */
    .sg-spin { width:15px; height:15px; border-radius:50%; flex-shrink:0;
      border:2px solid rgba(255,255,255,.3); border-top-color:#fff;
      animation:sg-spin .7s linear infinite; }

    /* ── Responsive ── */
    @media(max-width:768px){
      .sg-left  { display:none !important; }
      .sg-right { width:100% !important; padding:28px 18px 36px !important; }
      .sg-mob   { display:block !important; }
    }
    @media(min-width:769px){ .sg-mob { display:none !important; } }
  `}</style>
);

/* ─────────────────────────────────────────────
   MAIN COMPONENT — ALL LOGIC UNCHANGED
───────────────────────────────────────────── */
export default function Signup() {
  const [form, setForm] = useState({
    businessName:"", businessType:"Hotel", businessCategory:"Hotel",
    email:"", password:"", phoneNumber:"", whatsappNumber:"",
    address:"", state:"", termsAccepted:false,
  });

  const indianStates = [
    "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh",
    "Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka",
    "Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram",
    "Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
    "Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
    "Andaman and Nicobar Islands","Chandigarh",
    "Dadra and Nagar Haveli and Daman and Diu","Delhi",
    "Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry",
  ];

  const navigate = useNavigate();
  const slugify = (name) => name.toLowerCase().trim().replace(/[^a-z0-9 ]/g,"").replace(/\s+/g,"-");
  const getNextPaymentDate = () => { const d=new Date(); d.setMonth(d.getMonth()+1); return d.toISOString().slice(0,10); };
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const restaurantMenu = [
    {name:"Veg Burger",desc:"Grilled veg patty with lettuce & mayo",cat1:"Food",cat2:"Snacks",price:120,type:"Veg"},
    {name:"Paneer Butter Masala",desc:"Creamy tomato gravy with paneer",cat1:"Food",cat2:"Main Course",price:240,type:"Veg"},
    {name:"Veg Biryani",desc:"Aromatic rice with spices & vegetables",cat1:"Food",cat2:"Main Course",price:210,type:"Veg"},
    {name:"Masala Dosa",desc:"Crispy dosa with spiced potato filling",cat1:"Food",cat2:"Main Course",price:110,type:"Veg"},
    {name:"Pasta Alfredo",desc:"Creamy white sauce pasta",cat1:"Food",cat2:"Continental",price:260,type:"Veg"},
    {name:"French Fries",desc:"Crispy salted fries",cat1:"Food",cat2:"Snacks",price:90,type:"Veg"},
    {name:"Cold Coffee",desc:"Chilled coffee with ice cream",cat1:"Drinks",cat2:"Cold Beverages",price:120,type:"drink"},
    {name:"Masala Chai",desc:"Indian spiced tea",cat1:"Drinks",cat2:"Hot Beverages",price:40,type:"drink"},
    {name:"Fresh Lime Soda",desc:"Refreshing lemon soda",cat1:"Drinks",cat2:"Cold Beverages",price:70,type:"drink"},
    {name:"Chocolate Milkshake",desc:"Rich chocolate shake",cat1:"Drinks",cat2:"Cold Beverages",price:150,type:"drink"},
  ];

  const hotelMenu = [
    {name:"Room Service Thali",desc:"Complete veg meal",cat1:"Food",cat2:"Room Dining",price:350,type:"Veg"},
    {name:"Club Sandwich",desc:"Triple layered sandwich",cat1:"Food",cat2:"Room Dining",price:220,type:"Veg"},
    {name:"Mineral Water",desc:"1 litre bottled water",cat1:"Drinks",cat2:"Beverages",price:40,type:"drink"},
    {name:"Fresh Orange Juice",desc:"Freshly squeezed juice",cat1:"Drinks",cat2:"Beverages",price:140,type:"drink"},
    {name:"Swedish Massage",desc:"60 min full body massage",cat1:"SPA",cat2:"Massage",price:1800,type:"Spa"},
    {name:"Aroma Therapy",desc:"Relaxing aroma oils therapy",cat1:"SPA",cat2:"Therapy",price:2200,type:"Spa"},
    {name:"Airport Pickup",desc:"Sedan airport transfer",cat1:"Local Taxi",cat2:"Transfers",price:1200,type:"Taxi"},
    {name:"City Tour (Half Day)",desc:"4 hour city sightseeing",cat1:"Local Taxi",cat2:"Tour",price:2000,type:"Taxi"},
    {name:"Local Sightseeing Tour",desc:"Guided heritage tour",cat1:"Book Experience",cat2:"Tours",price:1500,type:"Experience"},
    {name:"Candle Light Dinner",desc:"Private dinner setup",cat1:"Book Experience",cat2:"Dining",price:3500,type:"Experience"},
    {name:"Laundry (Per Cloth)",desc:"Wash & iron",cat1:"Laundry",cat2:"Services",price:50,type:"Laundry"},
    {name:"Express Laundry",desc:"Same day delivery",cat1:"Laundry",cat2:"Services",price:120,type:"Laundry"},
  ];

  const buildMenu = (items) => items.map((i) => ({
    "Item Name":i.name,"Item Description":i.desc,"Level 1":i.cat1,
    "Level 2":i.cat2,"Price":i.price,"Veg_NonVeg":i.type,
    imageUrl:"",itemCode:"",categoryCode:"",available:true,
  }));

  const getSampleMenuByBusinessType = (businessType) => {
    if (businessType==="Restaurant"||businessType==="Cafe") return buildMenu(restaurantMenu);
    if (businessType==="Hotel") return buildMenu(hotelMenu);
    return [];
  };

  const validateSignup = async () => {
    const emailSnap = await getDoc(doc(db,"userDetails",form.email));
    if (emailSnap.exists()) return "Email already registered.";
    const slug = slugify(form.businessName);
    const slugSnap = await getDoc(doc(db,"menus",slug));
    if (slugSnap.exists()) return "Business name already exists.";
    return null;
  };

  const isValidIndianNumber = (num) => /^91\d{10}$/.test(num);
  const blockedDomains = ["mailinator.com","tempmail.com","10minutemail.com","guerrillamail.com","yopmail.com","dispostable.com","trashmail.com"];
  const isDisposableEmail = (email) => blockedDomains.some((d) => email.toLowerCase().endsWith("@"+d));

  const handleSignup = async () => {
    if (isSigningUp) return;
    setIsSigningUp(true);
    try {
      if (!form.businessName||!form.email||!form.password||!form.phoneNumber||!form.whatsappNumber||!form.address||!form.state) { toast.error("All fields are mandatory"); return; }
      if (!isValidIndianNumber(form.phoneNumber)) { toast.error("Phone number must be 12 digits and start with 91"); return; }
      if (!isValidIndianNumber(form.whatsappNumber)) { toast.error("WhatsApp number must be 12 digits and start with 91"); return; }
      if (isDisposableEmail(form.email)) { toast.error("Temporary email addresses are not allowed"); return; }
      if (!form.termsAccepted) { toast.error("You must agree to Terms & Conditions to continue"); return; }
      if (!form.businessType) { toast.error("Please select Business Type"); return; }
      const err = await validateSignup();
      if (err) return toast.error(err);
      const rate = await fetch("https://us-central1-zafflostarter.cloudfunctions.net/checkSignupRateLimit",{method:"POST"});
      const rateResult = await rate.json();
      if (!rateResult.success) { toast.error(rateResult.message||"Too many signup attempts. Try again later."); return; }
      const slug = slugify(form.businessName);
      const response = await fetch("https://us-central1-zafflostarter.cloudfunctions.net/createUserWithHashedPassword",{
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          email:form.email, password:form.password,
          userData:{
            businessName:form.businessName, businessType:form.businessType,
            businessSubType:["Service Business","Salon","Spa"].includes(form.businessCategory)?"service":"",
            phoneNumber:form.phoneNumber, whatsappNumber:form.whatsappNumber,
            address:form.address, state:form.state, gstNumber:"",
            termsAccepted:true, termsAcceptedAt:new Date().toISOString(),
            role:"user", status:"active", onboardingCompleted:false,
            homeDeliveryAvailable:form.businessType==="Restaurant",
            takeAwayAvailable:false, menuShown:0,
            lastPaymentAmount:"0", lastPaymentDate:new Date().toISOString(),
            nextPaymentAmount:"399", nextPaymentDate:getNextPaymentDate(),
            logo:"", accountType:"TRIAL",
          },
        }),
      });
      const result = await response.json();
      if (!response.ok) { toast.error(result.error||"Signup failed"); return; }
      await setDoc(doc(db,"menus",slug),{email:form.email, englishMenu:getSampleMenuByBusinessType(form.businessType)});
      fetch("https://us-central1-zafflostarter.cloudfunctions.net/createSalesforceLead",{
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({businessName:form.businessName, phoneNumber:form.phoneNumber, address:form.address, email:form.email}),
      }).catch(()=>{});
      toast.success("🎉 Account created successfully! Redirecting to login...",{autoClose:2500});
      setTimeout(()=>{ navigate("/login"); },2500);
      trackEvent("signup_completed", {user_type: "owner"});
    } catch(err) {
      console.error(err); toast.error("Signup failed. Please try again.");
    } finally { setIsSigningUp(false); }
  };

  const btnClass = isSigningUp ? "sg-btn load" : form.termsAccepted ? "sg-btn on" : "sg-btn off";

  const features = [
    {emoji:"🛒",title:"WhatsApp Ordering",    sub:"Instant orders from QR scan"},
    {emoji:"📊",title:"Sales Analytics",       sub:"Revenue, top items & peak hours"},
    {emoji:"🧾",title:"GST Billing",           sub:"Auto-generate compliant invoices"},
    {emoji:"📅",title:"Appointments",          sub:"Salon & spa booking via WhatsApp"},
    {emoji:"🎁",title:"Customer Offers",       sub:"Campaigns to drive repeat visits"},
  ];

  /* ── Compact field wrapper ── */
  const G = ({children, style={}}) => (
    <div style={{marginBottom:10, ...style}}>{children}</div>
  );

  return (
    <>
      <Styles/>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar/>

      <div className="sg-root" style={{minHeight:"100vh", display:"flex", background:"#f5f5f2"}}>

        {/* ══════════════════════════════════
            LEFT — dark brand panel
        ══════════════════════════════════ */}
        <div className="sg-left sg-panel-left" style={{
          width:"40%", flexShrink:0,
          background:"linear-gradient(160deg,#0c1a0c 0%,#152815 55%,#0a120a 100%)",
          padding:"40px 36px",
          display:"flex", flexDirection:"column", justifyContent:"space-between",
          position:"relative", overflow:"hidden",
        }}>
          {/* grid */}
          <div style={{position:"absolute",inset:0,pointerEvents:"none",
            backgroundImage:"linear-gradient(rgba(108,187,35,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(108,187,35,.04) 1px,transparent 1px)",
            backgroundSize:"48px 48px"}}/>
          {/* glow */}
          <div style={{position:"absolute",top:"-20%",left:"50%",transform:"translateX(-50%)",
            width:480,height:380,borderRadius:"50%",
            background:"radial-gradient(ellipse,rgba(108,187,35,.14) 0%,transparent 70%)",
            pointerEvents:"none"}}/>
          {/* crosses */}
          {[[1,1],[1,0],[0,1],[0,0]].map(([t,l],i)=>(
            <div key={i} className="sg-cx" style={{
              position:"absolute", width:14, height:14,
              ...(t?{top:28}:{bottom:28}), ...(l?{left:28}:{right:28}),
            }}/>
          ))}
          {/* ghost Z */}
          <div className="sg-zh" style={{
            position:"absolute",bottom:-40,right:-20,
            fontSize:200,fontWeight:900,fontStyle:"italic",
            color:"#fff",opacity:.024,lineHeight:1,
            userSelect:"none",pointerEvents:"none",
          }}>Z</div>

          {/* TOP */}
          <div style={{position:"relative",zIndex:1}}>
            <div style={{marginBottom:28}}>
              <img src={LogoImage} alt="Zafflo" style={{height:30,width:"auto"}}/>
            </div>

            <h2 className="sg-zh" style={{
              fontSize:"clamp(24px,2.6vw,36px)",fontWeight:900,fontStyle:"italic",
              color:"#fff",lineHeight:1.1,letterSpacing:"-0.03em",marginBottom:10,
            }}>
              Start your{" "}
              <span className="sg-shine">free trial</span>
              <br/>today.
            </h2>
            <p style={{fontSize:12.5,color:"rgba(255,255,255,.38)",fontWeight:300,lineHeight:1.65,marginBottom:28,maxWidth:268}}>
              50+ Indian businesses growing on Zafflo's QR-powered platform.
            </p>

            {/* features */}
            {features.map(({emoji,title,sub})=>(
              <div key={title} className="sg-feat">
                <div className="sg-feat-ic"><span style={{fontSize:13}}>{emoji}</span></div>
                <div>
                  <div style={{fontSize:12.5,fontWeight:600,color:"#fff",marginBottom:1}}>{title}</div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,.32)",lineHeight:1.45}}>{sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* BOTTOM */}
          <div style={{position:"relative",zIndex:1}}>
            <div style={{height:1,background:"rgba(255,255,255,.07)",marginBottom:16}}/>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
              {[{v:"50+",l:"Businesses"},{v:"10K+",l:"Orders"},{v:"5.0★",l:"Rating"}].map(({v,l})=>(
                <div key={l}>
                  <div className="sg-zh" style={{fontSize:18,fontWeight:900,fontStyle:"italic",color:"#6cbb23",lineHeight:1}}>{v}</div>
                  <div style={{fontSize:9.5,color:"rgba(255,255,255,.24)",marginTop:2}}>{l}</div>
                </div>
              ))}
            </div>
            <div style={{
              display:"inline-flex",alignItems:"center",gap:6,
              background:"rgba(108,187,35,.11)",border:"1px solid rgba(108,187,35,.22)",
              borderRadius:999,padding:"5px 12px",
              fontSize:10.5,fontWeight:700,color:"#6cbb23",
            }}>
              <span style={{width:5,height:5,borderRadius:"50%",background:"#6cbb23",
                display:"inline-block",animation:"sg-pulse 1.6s ease-in-out infinite"}}/>
              1 Month Free · No Credit Card
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════
            RIGHT — compact form
        ══════════════════════════════════ */}
        <div className="sg-right sg-panel-right" style={{
          flex:1, minWidth:0, overflowY:"auto",
          display:"flex", flexDirection:"column", justifyContent:"center", alignItems:"center",
          padding:"36px 32px",
        }}>
          <div style={{width:"100%", maxWidth:400}}>

            {/* mobile header */}
            <div className="sg-mob" style={{marginBottom:20,textAlign:"center"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginBottom:10}}>
                <img src={LogoImage} alt="Zafflo" style={{height:28,width:"auto"}}/>
              </div>
              <span style={{
                display:"inline-flex",alignItems:"center",gap:5,
                background:"#f0f9e6",border:"1.5px solid #c8e89c",
                borderRadius:999,padding:"3px 11px",
                fontSize:10.5,fontWeight:700,color:"#3b6d11",
              }}>1 Month Free · No Card Needed</span>
            </div>

            {/* header */}
            <div style={{marginBottom:20}}>
              <h1 className="sg-zh" style={{
                fontSize:"clamp(22px,2.8vw,28px)",fontWeight:900,fontStyle:"italic",
                color:"#111",letterSpacing:"-0.02em",lineHeight:1.1,marginBottom:5,
              }}>Create your account</h1>
              <p style={{fontSize:12.5,color:"#aaa",fontWeight:300}}>
                Already have one?{" "}
                <a href="/login" style={{color:"#6cbb23",fontWeight:600,textDecoration:"none"}}>Sign in →</a>
              </p>
            </div>

            {/* ── FIELDS ── */}

            {/* Business Name + Type */}
            <div className="sg-f d1" style={{display:"flex",gap:8,marginBottom:10}}>
              <div style={{flex:"1 1 55%"}}>
                <label className="sg-lbl">Business Name</label>
                <input required placeholder="e.g. Café Bliss" className="sg-field"
                  onChange={e=>setForm({...form,businessName:e.target.value})}/>
              </div>
              <div style={{flex:"1 1 45%"}}>
                <label className="sg-lbl">Type</label>
                <select
                  className={`sg-field sg-sel${!form.businessCategory?" empty":""}`}
                  value={form.businessCategory||""}
                  onChange={e=>{
                    const s=e.target.value;
                    setForm({...form,businessCategory:s,businessType:s==="Hotel"?"Hotel":"Restaurant"});
                  }}
                >
                  <option value="">Select</option>
                  <option value="Hotel">Hotel</option>
                  <option value="Cafe">Cafe</option>
                  <option value="Cloud Kitchen">Cloud Kitchen</option>
                  <option value="Home Chef">Home Chef</option>
                  <option value="Bakery">Bakery</option>
                  <option value="Retail Store">Retail Store</option>
                  <option value="Salon">Salon</option>
                  <option value="Spa">Spa</option>
                  <option value="Service Business">Service Business</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {/* Email */}
            <div className="sg-f d2" style={{marginBottom:10}}>
              <label className="sg-lbl">Email Address</label>
              <input required placeholder="you@yourbusiness.com" className="sg-field"
                onChange={e=>setForm({...form,email:e.target.value})}/>
            </div>

            {/* Password */}
            <div className="sg-f d3" style={{marginBottom:10,position:"relative"}}>
              <label className="sg-lbl">Password</label>
              <input required type={showPass?"text":"password"} placeholder="Min 8 characters"
                className="sg-field" style={{paddingRight:40}}
                onChange={e=>setForm({...form,password:e.target.value})}/>
              <button type="button" onClick={()=>setShowPass(s=>!s)} style={{
                position:"absolute",right:10,bottom:9,background:"none",border:"none",
                cursor:"pointer",color:"#bbb",fontSize:11,
                fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:600,padding:"2px 3px",
              }}>{showPass?"Hide":"Show"}</button>
            </div>

            {/* Phone + WhatsApp */}
            <div className="sg-f d4" style={{display:"flex",gap:8,marginBottom:10}}>
              <div style={{flex:1}}>
                <label className="sg-lbl">Phone No.</label>
                <input required placeholder="91XXXXXXXXXX" className="sg-field"
                  onChange={e=>setForm({...form,phoneNumber:e.target.value})}/>
              </div>
              <div style={{flex:1}}>
                <label className="sg-lbl">WhatsApp No.</label>
                <input required placeholder="91XXXXXXXXXX" className="sg-field"
                  onChange={e=>setForm({...form,whatsappNumber:e.target.value})}/>
              </div>
            </div>

            {/* Address + State side by side on desktop */}
            <div className="sg-f d5" style={{display:"flex",gap:8,marginBottom:10,alignItems:"flex-start"}}>
              <div style={{flex:"1 1 100%"}}>
                <label className="sg-lbl">Business Address</label>
                <textarea required rows={2} placeholder="Street, area, city"
                  className="sg-field sg-ta"
                  onChange={e=>setForm({...form,address:e.target.value})}/>
              </div>
             
            </div>

             <div className="sg-f d4" style={{display:"flex",gap:8,marginBottom:10}}>
               <div style={{flex:"1 1 32%"}}>
                <label className="sg-lbl">State</label>
                <select required value={form.state}
                  className={`sg-field sg-sel${!form.state?" empty":""}`}
                  onChange={e=>setForm({...form,state:e.target.value})}
                   /* match textarea height */
                >
                  <option value="">Select state</option>
                  {indianStates.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              
            </div>

            {/* Divider */}
            <div style={{height:1,background:"#f0f0ec",margin:"14px 0"}}/>

            {/* Terms */}
            <div className="sg-f d7" style={{marginBottom:14}}>
              <label style={{display:"flex",alignItems:"flex-start",gap:9,cursor:"pointer"}}
                onClick={()=>setForm({...form,termsAccepted:!form.termsAccepted})}>
                <div className={`sg-chk${form.termsAccepted?" on":""}`}>
                  {form.termsAccepted&&(
                    <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                      <path d="M1 3l2 2.5L8 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <span style={{fontSize:11.5,color:"#888",lineHeight:1.55}}>
                  I agree to Zafflo's{" "}
                  <a href="/terms" target="_blank"
                    style={{color:"#6cbb23",fontWeight:600,textDecoration:"none"}}
                    onClick={e=>e.stopPropagation()}>Terms & Conditions</a>
                  {" "}and{" "}
                  <a href="/terms" target="_blank"
                    style={{color:"#6cbb23",fontWeight:600,textDecoration:"none"}}
                    onClick={e=>e.stopPropagation()}>Privacy Policy</a>
                </span>
              </label>
            </div>

            {/* Submit */}
            <div className="sg-f d8">
              <button className={btnClass} disabled={!form.termsAccepted||isSigningUp} onClick={handleSignup}>
                {isSigningUp?(
                  <span style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                    <span className="sg-spin"/>Creating your account…
                  </span>
                ):"Start 1 Month Free Trial →"}
              </button>

              {/* trust row */}
              <div style={{marginTop:10,display:"flex",justifyContent:"center",gap:16,flexWrap:"wrap"}}>
                {["No credit card","Setup in 5 min","Cancel anytime"].map(t=>(
                  <span key={t} style={{display:"flex",alignItems:"center",gap:4}}>
                    <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                      <path d="M1.5 4.5l2 2 4-3.5" stroke="#6cbb23" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span style={{fontSize:10.5,color:"#c0c0ba"}}>{t}</span>
                  </span>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}